import { RowDataTransaction } from 'ag-grid-community';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { BillingItemAlertSummary, BillingReportRow } from '@/pages/BillingReportResults/types';
import PaginatedResponse from '@/types/paginatedResponse';
import ajax from '@/utils/ajax';

import {
  AsyncActionResponse,
  BillingConfig,
  FlushLookupsResponse,
  LinkedItem,
  UpdateRowAction
} from './types';

const basePath = '/api/dugout/billing_items';

// The lookup cache is global rather than per-report, so flushing it lives on
// the report results viewset and is shared with the billing report view
// (BillingReportResultApp) instead of being duplicated per grid.
const billingReportResultsPath = '/api/billing/billing_report_results';

// Mirrors BillingItemPagination.max_page_size (billing_item_viewset.py).
const FETCH_ROWS_BY_IDS_CHUNK_SIZE = 200;

export const billingItemRepository = {
  /**
   * Lists billing items as raw cached row data — the same shape returned by
   * the billing report's rows endpoint — so both grids render identically.
   */
  async listBillingItems({
    host,
    page = '1',
    ordering,
    filters
  }: {
    host: string;
    page?: string;
    ordering?: string;
    filters?: Record<string, string | string[]>;
  }): Promise<PaginatedResponse<BillingReportRow[]>> {
    const params: Record<string, string> = {
      page,
      ...(ordering && { ordering })
    };

    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        params[key] = Array.isArray(value) ? value.join(',') : value;
      });
    }

    const response = await ajax.get({
      url: `${host}${basePath}/`,
      params
    });

    return response.data;
  },

  /**
   * Alert counts for the whole filtered set, not just the loaded page -- the
   * grid's infinite row model never holds enough rows to aggregate these
   * client-side the way the legacy report view does. Takes the same translated
   * filter params as listBillingItems, so the summary describes the same set of
   * items the grid is showing.
   */
  async getSummary({
    host,
    filters
  }: {
    host: string;
    filters?: Record<string, string | string[]>;
  }): Promise<BillingItemAlertSummary> {
    const params: Record<string, string> = {};

    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key];
        params[key] = Array.isArray(value) ? value.join(',') : value;
      });
    }

    const response = await ajax.get({
      url: `${host}${basePath}/summary/`,
      params
    });

    return response.data;
  },

  /**
   * Metadata about the billing lookup tables -- currently just the Google
   * Sheet they're maintained in, so the grid can link out to it.
   */
  async getBillingConfig({ host }: { host: string }): Promise<BillingConfig> {
    const response = await ajax.get({
      url: `${host}${basePath}/billing_config/`
    });

    return response.data;
  },

  /**
   * Drops the cached billing lookups (rates, service-line mappings) so the next
   * refresh or report generation re-reads them from the lookup sheet.
   */
  async flushLookups({ host }: { host: string }): Promise<FlushLookupsResponse> {
    const response = await ajax.post({
      url: `${host}${billingReportResultsPath}/flush_lookups/`,
      data: {}
    });

    return response.data as unknown as FlushLookupsResponse;
  },

  /**
   * Report-agnostic bulk refresh -- resolves each billing item's report result
   * server-side, so a selection spanning multiple reports can be refreshed in a
   * single request. Always runs as an async background task -- like
   * BillingReportResultViewset, results arrive over the billing-items Ably
   * channel instead of in the response, so a large cross-report selection
   * can't time out the request.
   */
  async refreshRows({
    host,
    billingItemIds
  }: {
    host: string;
    billingItemIds: string[];
  }): Promise<AsyncActionResponse> {
    const response = await ajax.post({
      url: `${host}${basePath}/refresh_rows/`,
      data: { billing_item_ids: billingItemIds }
    });
    return response.data as unknown as AsyncActionResponse;
  },

  async submitSelectedRows({
    host,
    selectedRows
  }: {
    host: string;
    selectedRows: BillingReportRow[];
  }): Promise<AsyncActionResponse> {
    const response = await ajax.post({
      url: `${host}${basePath}/submit_selected_rows/`,
      data: { selected_rows: selectedRows }
    });
    return response.data as unknown as AsyncActionResponse;
  },

  /**
   * Re-fetches specific billing items by ID, across reports. Used to pull the
   * updated cached rows after a billing-items channel event, without redoing
   * the full filter/sort/paginate flow of listBillingItems.
   *
   * Requests are chunked at BillingItemPagination.max_page_size (200) both to
   * stay under the endpoint's page size cap -- so every ID is returned rather
   * than just the first page's worth -- and to avoid an overlong URL from a
   * single comma-separated `billing_item_ids` param.
   */
  async fetchRowsByIds({
    host,
    billingItemIds
  }: {
    host: string;
    billingItemIds: string[];
  }): Promise<BillingReportRow[]> {
    if (billingItemIds.length === 0) return [];

    const chunks: string[][] = [];
    for (let i = 0; i < billingItemIds.length; i += FETCH_ROWS_BY_IDS_CHUNK_SIZE) {
      chunks.push(billingItemIds.slice(i, i + FETCH_ROWS_BY_IDS_CHUNK_SIZE));
    }

    const responses = await Promise.all(
      chunks.map((chunk) =>
        ajax.get({
          url: `${host}${basePath}/`,
          params: { billing_item_ids: chunk.join(','), size: chunk.length }
        })
      )
    );

    return responses.flatMap((response) => response.data.results);
  },

  async updateRows({
    host,
    data
  }: {
    host: string;
    data: UpdateRowAction[];
  }): Promise<RowDataTransaction> {
    const response = await ajax.post({
      url: `${host}${basePath}/update_rows/`,
      data: { data }
    });
    return response.data as RowDataTransaction;
  },

  async linkItems({
    host,
    linkedItems
  }: {
    host: string;
    linkedItems: LinkedItem[];
  }): Promise<RowDataTransaction> {
    const response = await ajax.post({
      url: `${host}${basePath}/link_items/`,
      data: { linked_items: linkedItems }
    });
    return response.data as RowDataTransaction;
  }
};

const BillingItemRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async listBillingItems({
      page,
      ordering,
      filters
    }: {
      page?: string;
      ordering?: string;
      filters?: Record<string, string | string[]>;
    }) {
      return billingItemRepository.listBillingItems({ host, page, ordering, filters });
    },

    async getSummary(filters?: Record<string, string | string[]>) {
      return billingItemRepository.getSummary({ host, filters });
    },

    async getBillingConfig() {
      return billingItemRepository.getBillingConfig({ host });
    },

    async flushLookups() {
      return billingItemRepository.flushLookups({ host });
    },

    async refreshRows(billingItemIds: string[]) {
      return billingItemRepository.refreshRows({ host, billingItemIds });
    },

    async submitSelectedRows(selectedRows: BillingReportRow[]) {
      return billingItemRepository.submitSelectedRows({ host, selectedRows });
    },

    async fetchRowsByIds(billingItemIds: string[]) {
      return billingItemRepository.fetchRowsByIds({ host, billingItemIds });
    },

    async updateRows(data: UpdateRowAction[]) {
      return billingItemRepository.updateRows({ host, data });
    },

    async linkItems(linkedItems: LinkedItem[]) {
      return billingItemRepository.linkItems({ host, linkedItems });
    }
  };
};

export default BillingItemRepository;
